import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import {UserPrivilegeService} from '../../../services/common/user-privilege/user-privilege.service';
import * as _ from 'lodash';
import {ThresholdApiService} from '../../../services/api/statistics/threshold/threshold-api.service';
import {ConfirmationDialogComponent} from '../../../dialogs/confirmation-dialog/confirmation-dialog.component';
import {MatDialog} from '@angular/material/dialog';
import {SnackBarService} from '../../../services/common/snack-bar/snack-bar.service';

@Component({
  selector: 'thresholds-list',
  templateUrl: './thresholds-list.component.html',
  styleUrls: ['./thresholds-list.component.scss']
})
export class ThresholdsListComponent implements OnInit {

  @Output() onSelection = new EventEmitter<string>();

  thresholds;
  selectedThreshold: any;
  isAuthorized: boolean;

  constructor(private thresholdApiService: ThresholdApiService,
              private userPrivilege: UserPrivilegeService,
              private snackBarService: SnackBarService,
              public dialog: MatDialog) { }

  ngOnInit(): void {
    this.isAuthorized = this.userPrivilege.isAuthorized();
    this.selectedThreshold = !_.isEmpty(this.thresholds) ? this.thresholds[0] : null;
    this.getThreshold();
  }

  addThreshold(): void {
    const numOfThresholds = this.thresholds.push({
      name: this.generateDefaultThresholdName(),
      elementType: 'arm',
      alarmSeverity: 'major',
      enable: true,
      thresholds: [],
      allElements: false
    });

    this.selectedThreshold = this.thresholds[numOfThresholds - 1];
    this.onSelection.emit(this.selectedThreshold);
  }

  deleteThreshold(): void {
    const nameToDelete = this.selectedThreshold.name;
    const confirmationMessage = `Are you sure you want to delete "${nameToDelete}" ?`;

    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: {
        message: confirmationMessage
      }
    });

    dialogRef.componentInstance.onApply.subscribe(() => {
      if (this.selectedThreshold.id) {
        const selectedThresholdId = this.thresholds.findIndex((threshold) => threshold.id === this.selectedThreshold.id);

        this.thresholdApiService.deleteThreshold(this.selectedThreshold.id).then(() => {
          this.selectedThreshold = selectedThresholdId > 0 ? this.thresholds[selectedThresholdId - 1] : null;
          this.snackBarService.display(`${nameToDelete} successfully deleted`);
          this.refreshThreshold();
        });
      } else {
        this.thresholds.pop();
        this.snackBarService.display(`${nameToDelete} successfully deleted`);

        if (this.thresholds.length > 0) {
          this.selectedThreshold = this.thresholds[this.thresholds.length - 1];
          this.onSelection.emit(this.selectedThreshold);
        } else {
          this.onThresholdSelection(null);
        }
      }

      dialogRef.close();
    });
  }

  refreshThreshold(): void {
    if (this.thresholds.find(t => _.isNil(t.id))) {
      const confirmMessage = 'There are unsaved changes, are you sure you want to refresh ?';
      const editConfDialog = this.dialog.open(ConfirmationDialogComponent, {
        disableClose: true,
        width: '460px',
        data: {
          message: confirmMessage
        }
      });

      editConfDialog.componentInstance.onApply.subscribe(() => {
        editConfDialog.close();
        this.getThreshold();
      });
    } else {
      this.getThreshold();
    }
  }

  onThresholdSelection(threshold: any): void {
    this.selectedThreshold = threshold;
    this.onSelection.emit(threshold);
  }

  showAddIcon(): boolean {
    return this.thresholds &&
        (this.thresholds.length === 0 ||
            !_.isNil(this.thresholds[this.thresholds.length - 1].id));
  }

  getThreshold(): void {
    this.thresholdApiService.getThresholds().then(thresholds => {
      this.thresholds = thresholds.elements;
      this.thresholds.forEach(threshold => {
        threshold.totalAlerts = threshold.thresholds.map(t => t.alarmsCount).reduce((t1, t2) => t1 + t2, 0);
      });

      if (_.isEmpty(this.thresholds)) {
        this.onThresholdSelection(null);
      } else {
        this.selectedThreshold = this.selectedThreshold ?
            this.thresholds.find(t => t.name === this.selectedThreshold.name) : this.thresholds[0];
        this.onThresholdSelection(this.selectedThreshold);
      }
    });
  }

  private generateDefaultThresholdName(): string {
    const defaultName = 'New Threshold';

    if (!this.thresholds.find(t => t.name === defaultName)) {
      return defaultName;
    }

    let numberOfDefaultNames = 2;

    while (this.thresholds.find(t => t.name === `${defaultName} ${numberOfDefaultNames}`)) {
      numberOfDefaultNames++;
    }

    return `${defaultName} ${numberOfDefaultNames}`;
  }
}
